import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, RefreshCw, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PulseLineSkeleton } from "@/components/animations/PulseLine";
import { usePortfolioStatus } from "@/hooks/useCompanyData";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

const Portfolio = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: companies = [], isLoading: loading, isRefetching } = usePortfolioStatus();

  const totalRed = companies.reduce((sum, c) => sum + c.red, 0);
  const totalAmber = companies.reduce((sum, c) => sum + c.amber, 0);
  const totalGreen = companies.reduce((sum, c) => sum + c.green, 0);
  const totalKPIs = companies.reduce((sum, c) => sum + c.total, 0);

  const TrendIcon = ({ trend }: { trend: "up" | "down" | "stable" }) => {
    if (trend === "up") return <TrendingUp className="h-4 w-4 text-success" />;
    if (trend === "down") return <TrendingDown className="h-4 w-4 text-danger" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <AppShell title="Portfolio" subtitle={`${companies.length} companies`} showBack>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <Card className="rounded-2xl">
            <CardHeader className="p-4 pb-2">
              <CardDescription className="text-xs">Total KPIs</CardDescription>
              <CardTitle className="text-2xl tabular-nums">{totalKPIs}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="rounded-2xl border-l-4 border-l-danger">
            <CardHeader className="p-4 pb-2">
              <CardDescription className="text-xs">Need Attention</CardDescription>
              <CardTitle className="text-2xl tabular-nums text-danger">{totalRed}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="rounded-2xl border-l-4 border-l-warning">
            <CardHeader className="p-4 pb-2">
              <CardDescription className="text-xs">Watch</CardDescription>
              <CardTitle className="text-2xl tabular-nums text-warning">{totalAmber}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="rounded-2xl border-l-4 border-l-success">
            <CardHeader className="p-4 pb-2">
              <CardDescription className="text-xs">On Track</CardDescription>
              <CardTitle className="text-2xl tabular-nums text-success">{totalGreen}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Refresh */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Company Overview
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => qc.invalidateQueries({ queryKey: ["portfolio-status"] })}
            disabled={loading || isRefetching}
            className="rounded-xl"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="rounded-2xl animate-pulse">
                <CardHeader className="p-5">
                  <div className="h-5 bg-muted rounded-lg w-3/4 mb-3" />
                  <div className="h-2 bg-muted rounded-full w-full mb-3" />
                  <PulseLineSkeleton className="h-4" />
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : companies.length === 0 ? (
          <Card className="rounded-3xl border-dashed border-2 bg-card/50">
            <div className="py-16 text-center">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No companies yet</h3>
              <p className="text-muted-foreground mb-6 text-sm">Create your first company to see it here</p>
              <Button onClick={() => navigate("/app/companies/new")} className="rounded-2xl">Create Company</Button>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {companies.map((company, index) => (
              <Card
                key={company.id}
                className={cn(
                  "cursor-pointer rounded-2xl border-border/50 transition-all duration-300",
                  "hover:shadow-lg hover:border-primary/20 hover:bg-card-hover",
                  "active:scale-[0.99]"
                )}
                onClick={() => navigate(`/app/companies/${company.id}`)}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardHeader className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <CardTitle className="text-base font-semibold truncate">{company.name}</CardTitle>
                    <TrendIcon trend={company.trend} />
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden flex">
                    {company.green > 0 && <div className="h-full bg-success transition-all" style={{ width: `${(company.green / company.total) * 100}%` }} />}
                    {company.amber > 0 && <div className="h-full bg-warning transition-all" style={{ width: `${(company.amber / company.total) * 100}%` }} />}
                    {company.red > 0 && <div className="h-full bg-danger transition-all" style={{ width: `${(company.red / company.total) * 100}%` }} />}
                  </div>
                  <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                    <span>{company.total} KPIs</span>
                    <div className="flex items-center gap-2">
                      {company.green > 0 && <span className="text-success">🟢 {company.green}</span>}
                      {company.amber > 0 && <span className="text-warning">🟡 {company.amber}</span>}
                      {company.red > 0 && <span className="text-danger">🔴 {company.red}</span>}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default Portfolio;
